import React, { useState, useEffect } from 'react';
import { ComponentCategory, ComponentSchema } from './types';
import { Search, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import axios from 'axios';
import type { SiteComponentFilter } from '../../services/api';

interface Props {
  onAddComponent: (componentId: string, variant?: string, defaults?: Record<string, any>) => void;
  /** Restrict the catalogue to a server-side subset, e.g. "imported" → only components insertable
   *  into an imported static site. Omit for the full Site Builder catalogue. */
  filterFor?: SiteComponentFilter;
}

const getApiBase = () =>
  process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8001`;

const ComponentBrowser: React.FC<Props> = ({ onAddComponent, filterFor }) => {
  const [categories, setCategories] = useState<ComponentCategory[]>([]);
  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.get(`${getApiBase()}/site-builder/components`, {
      headers: { Authorization: `Bearer ${token}` },
      params: filterFor ? { for: filterFor } : undefined,
    }).then(res => {
      setCategories(res.data.categories || []);
      if (res.data.categories?.length) setExpandedCat(res.data.categories[0].id);
    }).catch(() => {});
  }, [filterFor]);

  const filtered = search
    ? categories.map(cat => ({
        ...cat,
        components: cat.components.filter(c =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.id.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(cat => cat.components.length > 0)
    : categories;

  return (
    <div className="component-browser" style={{
      width: 240, borderRight: '1px solid rgba(0,0,0,0.08)',
      display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
    }}>
      <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--panel-strong)', borderRadius: 6, padding: '6px 10px',
        }}>
          <Search size={14} color="var(--text-secondary)" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search components..."
            style={{
              border: 'none', background: 'transparent', outline: 'none',
              fontSize: '0.8125rem', width: '100%',
            }}
          />
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {filtered.map(cat => (
          <div key={cat.id}>
            <button
              onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {expandedCat === cat.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {cat.label}
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {cat.components.length}
              </span>
            </button>
            {expandedCat === cat.id && (
              <div style={{ paddingBottom: 4 }}>
                {cat.components.map(comp => (
                  <ComponentItem key={comp.id} component={comp} onAdd={onAddComponent} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ComponentItem: React.FC<{
  component: ComponentSchema;
  onAdd: (id: string, variant?: string, defaults?: Record<string, any>) => void;
}> = ({ component, onAdd }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 12px 6px 28px', cursor: 'pointer',
      fontSize: '0.8125rem', color: 'var(--text)',
      transition: 'background 0.1s',
    }}
    onClick={() => {
      // Seed the new section's controls with the schema's defaults (prepopulated
      // content), matching the Landing Page palette's defaultProps behaviour.
      const defaults: Record<string, any> = {};
      for (const [k, def] of Object.entries(component.controls || {})) {
        if (def && (def as any).default !== undefined && (def as any).default !== null) {
          defaults[k] = (def as any).default;
        }
      }
      onAdd(component.id, component.variants[0] || '', defaults);
    }}
    onMouseEnter={e => (e.currentTarget.style.background = 'var(--panel-strong)')}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    title={component.description}
  >
    <span style={{ flex: 1 }}>{component.name}</span>
    <Plus size={14} color="var(--text-secondary)" />
  </div>
);

export default ComponentBrowser;
